/* =============================================================================
   Homepage narrative copy — single source of truth for the landing
   (src/FactoryOSLanding.jsx) and its components. Edit copy here, not in markup.

   EDITORS' MAP — one product, two jobs:
   - Above the fold sells AGENCY: Factory OS drives the engineering loop
     ("self-driving", walks the cycle) with the engineer approving each step.
   - Below the fold sells the HARBOR: inspectable, reconstructable traces for
     humans and AI across the full design cycle. "Harbor" is this map's word
     only — on the page, say "the record".
   - The hero sub's last clause ("keeps the why for Rev B") is the handshake
     between the two. Don't expand memory language in the hero.
   - One product runs through everything: the DRV-400 drive module, a
     sub-assembly of RBT-100 Warehouse robot. Hero demo, walk and both MIL tabs
     belong to that program, and the system around it shows through
     breadcrumbs, requirement flow-down, an interface change that ripples up to
     the chassis, and the product tree.

   Canon: Factory OS v3.1.0 (ratified Sep 28, 2026).
   - Mission: the fastest way from an engineering idea to validated hardware.
   - Problem: lost engineering why. Mechanism: every design cycle starts with
     more context than the last; compounding is how the loop gets faster.
   - For hardware teams of about 5 to 25 engineers — the people doing the
     engineering. Do not lead with defense or aerospace.

   Hard rules for anything written here:
   - AI proposes; engineers approve. MIL never makes anything true on its own.
   - DELIBERATE OVERRIDE: the canon lists "self-driving" under words to avoid.
     Kris approved it for the hero on Sep 29, 2026, on one condition — the
     approval is stated in the same viewport ("with you approving each step").
     Don't remove that clause, and don't "fix" the headline back.
   - "Autonomous" stays banned near MIL — including "autonomous mobile robot":
     say "warehouse robot".
   - Never a chatbot. Show the Thread (brief, plan, seven-step strip), never a
     chat window.
   - Factory OS sits beside CAD, PLM, ERP and QMS. It never replaces them.
   - "Validated" = released and field-tested. Bench evidence is "verified".
     A prediction is never either.
   - The seven steps are steps in a loop — never phases, stages, tabs or gates.
   - AMENDED (Sep 30, 2026, Kris): step 02 Research is broader than recall.
     It follows the SAE Baja cycle's R&D phase (designPhasesExplainerConfig):
     start from what the organization knows, then answer what it doesn't with
     trade studies and experiments on previous builds or surrogates, every
     study tracing to a requirement or risk. This replaces the canon line
     that limited Research to surfacing what the organization already knows.
   - Name no competitor or vendor. Describe categories ("your CAD").
   - No invented traction, customers, logos, quotes or metrics. Agents appear
     only in future tense and never acting without approval.
   ============================================================================= */

export const CATEGORY = 'the guided engineering loop for hardware';

export const HERO = {
  h1: 'Self-driving for hardware engineering.',
  sub: 'Describe what you’re building. Factory OS walks requirements, design, analysis and test with you approving each step, and keeps the why for Rev B.',
  audience: 'Built for hardware teams.',
  ctaPrimary: 'Request access',
  ctaSecondary: 'See how it works',
  /* The payoff line — shown after the walk, not in the hero. */
  supporting: 'We don’t just get you to Rev A. We remember why it was that Rev A, so Rev B starts ahead.',
  /* "Watch the loop" links only to a real recording of the product. None is
     published yet, so the button is omitted — set a URL here to show it. */
  watchUrl: null,
};

/* The live illustration beside the hero: a goal stated on a new sub-assembly
   under the robot (a field on the project, never a chat bubble), then MIL's
   plan filling in. Counts match the walk (Beat 2's six requirements, Beat 4's
   gear-type and controller decisions). Drafted items carry tags, not green
   checks — nothing here has evidence or approval yet. */
export const HERO_DEMO = {
  loop: ['Describe', 'Require', 'Design', 'Analyze', 'Test', 'Learn'],
  parent: 'New sub-assembly under RBT-100 Warehouse robot',
  prompt: 'What are you building?',
  goal: 'A drive module for a warehouse robot: 300 kg payload, 1.5 m/s, two shifts a day.',
  working: 'MIL is building your engineering plan…',
  plan: [
    { text: '6 requirements', state: 'drafted' },
    { text: '3 unknowns', state: 'identified' },
    { text: '2 architecture decisions', state: 'needed' },
    { text: 'First prototype', state: 'next' },
  ],
  footer: 'Nothing is accepted until you approve it.',
  /* After Require, each step shows its visual (technical drawings, not
     renders: CAD linework, FEA contact stress, the test rig, predicted vs
     measured), then MIL's one-line result, then Approve presses itself and a
     record drops into "Kept for Rev B". Numbers match the walk. */
  steps: [
    { key: 'describe' },
    { key: 'require', mil: 'Drafted 6 requirements, 3 unknowns and 2 decisions.', action: 'Approve', done: 'Approved by you', record: ['REQ ×6', 'approved by you'] },
    { key: 'design', mil: 'Drafted the drive. Heat check 138 → 104 °C, fixed before asking. Spur gears chosen.', action: 'Approve', done: 'Approved by you', record: ['D-006', 'spur over helical · reason kept'] },
    { key: 'analyze', mil: 'Gear life 15,000 h against 12,000 required. Rests on one assumed input.', action: 'Approve', done: 'Approved by you', record: ['PRED-01', '15,000 h · 1 assumption open'] },
    { key: 'test', mil: 'TR-310: backlash 0.9° at 900 h. Limit 0.5°: not met.', action: 'Record result', done: 'Recorded by you', record: ['TR-310', 'not met · recorded'] },
    { key: 'learn', mil: 'The floor hits at 95 N·m, not 25. Recomputed 1,050 h vs 900 actual: explained.', action: 'Approve', done: 'Approved by you', record: ['L-11', 'floor shock ≈ 4× · every future drive'] },
  ],
  recordsLabel: 'Kept for Rev B',
  finale: 'Rev B starts with 5 records.',
};

/* ── THE WALK ──────────────────────────────────────────────────────────────
   Directly under the hero: the DRV-400 drive module of RBT-100 Warehouse
   robot, from a sentence through a bench prototype, a Rev A that wears out
   on the rig, and a Rev B validated on a working warehouse floor — then the
   lesson reaching the next team. Written to "you". An illustration of the
   product, never a case study: no named company, customer, person or vendor.

   The days are the example's own clock — never turn them into speed claims.
   The numbers, checked so an engineer who does the math gets the same answer:
     Ø200 mm wheel at 1.5 m/s = 143 rpm; 20:1 → 2,865 motor rpm (rated 3,000).
     450 kg laden robot, two drive modules. Smooth-floor design load 12 N·m
     continuous, 25 N·m peak (a 1.0 m stop from 1.5 m/s = 1.13 m/s²).
     Floor-joint impact measured 95 N·m ≈ 3.8× the design peak ("nearly four").
     Motor 138 °C → 104 °C, limit 120 °C, at 45 °C ambient.
     Dyno: drag 0.9 N·m, efficiency 82%, encoder 0.05°, noise 4% → 0 errors.
     Gear life: req 12,000 h (250 d × 16 h × 3 yr); Rev A predicted 15,000,
     failed 900; recomputed 1,050 (16.7%, inside ±20%); Rev B 45,000.
     Backlash: req 0.5° (0.87 mm at the wheel, inside a ±5 mm dock);
     Rev A 0.2° → 0.9° at 900 h; Rev B 0.3°. Rev B gear peak 30 predicted,
     32 measured. Power ≤180 W: Rev A 150, Rev B 160.
     Days: rig Day 16 + 900 h ÷ 24 = Day 54; fleet Day 65 + 1,000 h ÷ 16 = 128.

   "Validated" appears in the walk title and on Beat 11, where it is earned. Strip statuses: 'met' (✓ green, only
   with evidence), 'open' (! amber), 'not' (· grey dashed). A beat with
   several `body` parts (4, 7, 10, 11) steps its screen through matching
   states as each part reaches mid-viewport. */
export const WALK = {
  eyebrow: 'Follow one build',
  title: 'From an idea to validated hardware that runs for years',
  intro: 'A warehouse robot is only as reliable as the wheels under it. Scroll through one drive module: a prototype on the bench, a Rev A that wears out, and a Rev B that holds up on the floor.',
  crumbs: ['RBT-100 Warehouse robot', 'DRV-400 Drive module (×2)'],
  beats: [
    {
      day: 1, step: 'The idea', rev: 'A',
      headline: 'Say what you’re building.',
      body: ['Every robot in the fleet stands on two of these. You open a project for the drive module that moves it: 300 kg of payload, 1.5 m/s, two shifts a day. MIL answers with a plan you can read, and every step names who does it: MIL, the calculation engine, or you.'],
      note: 'Nothing touches requirements or geometry until I accept this.',
      strip: ['not', 'not', 'not', 'not', 'not', 'not', 'not'],
    },
    {
      day: 1, step: 'Requirements', rev: 'A',
      headline: 'MIL drafts the requirements. You fix the two that matter.',
      body: ['MIL drafts six requirements, including wheel position held to 0.1° and a gearbox service every six months. You change both. The dock only needs ±5 mm, so 0.5° is plenty. And every service pulls a robot off the floor for a shift, so the drive has to run three years untouched. Each one traces up to a robot requirement it serves. Your corrections and your reasons stay with them.'],
      note: 'Every service pulls a robot off the floor. Three years, untouched.',
      strip: ['met', 'not', 'not', 'not', 'not', 'not', 'not'],
    },
    {
      day: 2, step: 'Research', rev: 'A',
      headline: 'It remembers what your team learned, and names what it hasn’t.',
      body: ['MIL surfaces a lesson from the team’s pallet mover: an encoder on the motor shaft hid gearbox backlash and let odometry drift, so put the encoder on the wheel. Then it looks for what nobody knows yet. There’s no measured impact torque for a loaded wheel crossing floor joints, only a smooth-floor estimate. MIL proposes a study: instrument the old pallet mover and drive it over a real warehouse floor, a week of rig time. You defer it to hold the schedule, and the risk stays open where everyone can see it.'],
      note: 'Deferred, not forgotten.',
      strip: ['met', 'met', 'not', 'not', 'not', 'not', 'not'],
    },
    {
      day: 4, step: 'Design', rev: 'A',
      headline: 'MIL drafts the drive, and catches its own mistake.',
      body: [
        'MIL drafts the drive: a 48 V brushless motor, a 20:1 two-stage spur gearbox, and a 200 mm polyurethane-tread wheel on a double-row bearing, with the encoder on the wheel shaft and the motor controller inside the housing. The engine checks the heat. Sealed in the housing with its controller at 45 °C, the motor would reach 138 °C, over its 120 °C limit.',
        'MIL moves the controller into its own finned pocket, adds a thermal pad to the chassis, and gets the motor to 104 °C before it asks you.',
        'You apply the geometry with your sign-in.',
      ],
      note: 'It fixed that before I ever saw it.',
      strip: ['met', 'met', 'met', 'not', 'not', 'not', 'not'],
    },
    {
      day: 9, step: 'Prototype', rev: 'A',
      headline: 'Build a prototype. Measure what you can.',
      body: ['You print the gears and run the drive on the wheel dyno. Drag comes in at 0.9 N·m, efficiency at 82%, and the encoder reads true to 0.05°. The dyno finds a problem early: at full current, motor switching noise corrupts 4% of the encoder counts. The encoder moves to differential signalling on a shielded cable, and the errors go to zero. One input is still assumed: how hard the floor hits the wheel.'],
      note: 'Cheaper to find that on the bench than in the warehouse.',
      strip: ['met', 'met', 'met', 'open', 'not', 'not', 'not'],
    },
    {
      day: 10, step: 'Analysis', rev: 'A',
      headline: 'Predict it, with the assumptions in the open.',
      body: ['The engine predicts 15,000 hours of gear life, 0.2° of backlash and 150 W per module. The gear life rests on the assumed floor load, and the wear method is new for these gears here, so both are marked. On Day 12 you release Rev A with your sign-in.'],
      note: 'Every number says what it rests on.',
      strip: ['met', 'met', 'met', 'open', 'met', 'not', 'not'],
    },
    {
      day: 54, step: 'Life test', rev: 'A',
      headline: 'The life test answers the question you set before it ran.',
      body: [
        'Rev A runs on the life-test rig under a 300 kg load, replaying a route recorded on a real warehouse floor, floor joints and dock plates included. The pass line was set on Day 10: backlash stays under 0.5° for the whole test. At 900 hours it reaches 0.9°.',
        'The teardown shows the first-stage pinion teeth pitted.',
      ],
      note: 'The line was drawn before the rig started.',
      strip: ['met', 'met', 'met', 'open', 'met', 'open', 'not'],
    },
    {
      day: 55, step: 'Wash-down test', rev: 'A',
      headline: 'The failure arrives with its history.',
      body: ['The wash-down test after the life run finds water at the encoder. Floor grit has worn a groove in the shaft seal. The failure opens a Thread that already holds the history, the parts it touches, and a similar failure from the team’s 2024 pallet mover: a shaft seal worn through by floor grit after five months of nightly scrubbing, fixed with a V-ring excluder and a labyrinth.'],
      note: 'Someone already solved this. Found in minutes.',
      strip: ['met', 'met', 'met', 'open', 'met', 'open', 'not'],
    },
    {
      day: 57, step: 'Correlation', rev: 'A',
      headline: 'Right about the gears. Wrong about the floor.',
      body: ['Predicted 15,000 hours, failed at 900. The rig’s torque log shows impact peaks of 95 N·m where the floor joints hit, nearly four times the smooth-floor estimate the deferred study would have checked. Rerun with the measured spectrum, the wear model predicts 1,050 hours, within 17% of what happened. You approve the explanation, and it becomes a lesson every future drive starts with.'],
      note: 'Now we know. So does the next project.',
      strip: ['met', 'met', 'met', 'met', 'met', 'open', 'met'],
    },
    {
      day: 61, step: 'Rev B', rev: 'B',
      headline: 'Rev B starts where Rev A learned.',
      body: [
        'Rev B takes the shock off the gears: an elastomer mount between the module and the chassis, so floor joints never reach the teeth. The helical gears Rev A rejected come back, and their reason is still on record: they needed a thrust bearing and better sealing to keep grease in, which the new labyrinth and excluder now provide. The prototype’s differential encoder stays.',
        'The mount also moves where the drive bolts to the chassis, so Factory OS opens a Thread on CHS-200 for the chassis owner to approve the new interface. The engine puts peak torque at the gears at 30 N·m and gear life at 45,000 hours. Once the chassis owner approves, you release Rev B with your sign-in.',
      ],
      note: 'The option we rejected had the right answer. Its reason was on file.',
      strip: ['met', 'met', 'met', 'met', 'met', 'not', 'not'],
    },
    {
      day: 128, step: 'On the floor', rev: 'B',
      headline: 'Rev B holds up in the field.',
      body: [
        'Rev B runs on robots in a working warehouse for 1,000 hours. Backlash holds at 0.3°. It stays dry through nightly wash-downs, and the encoder counts are clean. Peak gear torque over floor joints is 32 N·m against the 30 predicted. Released and field-tested, it is validated. The wear model now has two correlations; one more and gear life can be verified by analysis instead of a three-year test.',
        'Then, Day 150: another team starts a drive for a 1,000 kg pallet robot. MIL’s first plan already puts in a compliant mount and sizes the gears on the measured floor-shock spectrum. Nobody re-learns it.',
      ],
      note: 'Validated. And the next team starts ahead.',
      strip: ['met', 'met', 'met', 'met', 'met', 'met', 'met'],
    },
  ],
};

/* ── 1 · WHY REV B IS BETTER — the handoff from drive to record ──────────── */
export const WHY = {
  eyebrow: 'the why',
  headline: ['Your CAD remembers what you built.', 'Factory OS remembers why.'],
  cadence: ['The loop drives.', 'Every step writes a trace.', 'The next revision reads it.'],
  line: 'Every decision leaves a trace you can inspect. You and MIL read from the same record.',
  proof: 'The why is one click away on every part: a returning engineer can answer “why is this 12 mm?” in under a minute, without asking anyone.',
  /* ICP qualification lives here, one step below the hero. */
  audience: 'For hardware teams of 5 to 25 engineers: the people doing the engineering.',
};

/* ── 2 · THE SEVEN-STEP CYCLE, as a capture layer ────────────────────────
   Steps in a loop: never phases, stages, tabs or gates. Selectable;
   Correlation opens first. TREE shows every node running the same loop. */
export const FLOW = {
  eyebrow: 'the seven-step engineering cycle',
  headline: 'The full engineering design cycle, captured as a thread you can reconstruct.',
  sub: 'MIL walks every requirement and decision through the same seven steps. Each step writes to the record, so anyone can see what is met, what is missing, and how it got there.',
  steps: [
    { id: '01', name: 'Requirements', q: 'What must be true?', body: 'A named owner, the need in the requester’s own words, a required value, and how it will be proven. A requirement with no source is a wish.' },
    { id: '02', name: 'Research', q: 'What don’t we know yet?', body: 'Start from what your organization already knows, then answer what it doesn’t: trade studies, experiments on a previous build or a surrogate, and the options you ruled out, each with its reason. Every study traces to a requirement or a risk. Research that doesn’t change a decision is just activity.' },
    { id: '03', name: 'Design', q: 'Why this, and not that?', body: 'Every revision says why it happened. Every decision keeps the options it rejected, and why.' },
    { id: '04', name: 'Characterization', q: 'What are the real inputs?', body: 'Collect real inputs to earn the right to predict. Analysis on assumed inputs is guessing with decimal places.' },
    { id: '05', name: 'Analysis', q: 'What do you expect, and how sure are you?', body: 'Every prediction states its assumptions and its uncertainty. A method is trusted only after it has been proven against reality.' },
    { id: '06', name: 'Test', q: 'Did it cross the line?', body: 'A test answers a question against a pass/fail line set before the run. It does not exist to generate data.' },
    { id: '07', name: 'Correlation', q: 'Did reality match the prediction?', body: 'Compare the prediction to what happened and explain the gap. A model proven against reality can be trusted next time without re-testing. That is how engineering gets cheaper.' },
  ],
  defaultStep: 6,
  close: 'It is a loop, not a pipeline. Correlation feeds the next pass, so every revision starts better grounded than the last. Nothing moves through phase gates: the discipline is visible everywhere and blocks in one place, at release.',
};

/* The product tree beside the cycle. `at` = index of the step each node is
   on (0 Requirements … 6 Correlation); `done` marks a node whose revision has
   closed the loop. Only the drive opens to its parts. */
export const TREE = {
  headline: 'Every node runs the same loop.',
  line: 'Product, assembly, sub-assembly, part: each carries its own requirements, decisions, tests and trace, and changes flow between them.',
  nodes: [
    { id: 'RBT-100', name: 'Warehouse robot', level: 'product', depth: 0, at: 5, status: 'Integration test · Rev A' },
    { id: 'CHS-200', name: 'Chassis and frame', level: 'assembly', depth: 1, at: 2, status: 'Design · interface change from DRV-400 Rev B, approved' },
    { id: 'PWR-300', name: 'Battery and power', level: 'assembly', depth: 1, at: 6, status: 'Correlation' },
    { id: 'DRV-400', name: 'Drive module ×2', level: 'sub-assembly', depth: 1, done: true, status: 'Rev B · validated', focus: true },
    { id: 'MOT-410', name: 'Motor', level: 'part', depth: 2, done: true, status: 'Verified on the dyno' },
    { id: 'GBX-420', name: 'Gearbox', level: 'sub-assembly', depth: 2, done: true, status: 'Rev B · helical' },
    { id: 'GR-421', name: 'First-stage pinion', level: 'part', depth: 3, done: true, status: 'Rev B · Rev A pitted in TR-310' },
    { id: 'GR-422', name: 'Output gear', level: 'part', depth: 3, done: true, status: 'Rev B' },
    { id: 'WHL-430 · ENC-450 · CTL-460 · SEL-470 · MNT-490', name: '', level: 'parts', depth: 2, done: true, status: 'Rev B · each with its own record' },
    { id: 'LFT-500', name: 'Lift deck', level: 'assembly', depth: 1, at: 4, status: 'Analysis' },
    { id: 'NAV-600', name: 'Sensing and navigation', level: 'assembly', depth: 1, at: 5, status: 'Test' },
  ],
};

/* ── 3 · MIL — self-driving, not a black box ──────────────────────────────
   Two Threads in tabs: Planning (the walk's next team, with every carried-in
   lesson traced) and Diagnosing (a Rev B field report; MIL's read is a
   proposal that doesn't reopen a fixed problem). Never a chat window. */
export const MIL = {
  eyebrow: 'mil',
  headline: 'Self-driving, not a black box.',
  line: 'Every MIL proposal shows its trace: the requirements, unknowns and decisions it came from. You approve each step.',
  points: [
    'Tell MIL what you want to build. It answers with a plan you can read, and every step names who does it: MIL, the calculation engine, or you.',
    'Nothing touches requirements or geometry until you accept the plan.',
    'MIL drafts requirements, decisions, geometry and test plans. It checks its own drafts against your requirements before it asks you.',
    'You approve each requirement, decision, number and geometry change one at a time. Releasing a revision asks for your sign-in.',
    'Numbers you act on come from a deterministic engine with stated assumptions, not from the language model.',
    'Every step can be inspected afterwards: what MIL was asked, what it used, what it proposed, and what you decided.',
    'MIL also mentors. Ask it to challenge an assumption, explain a result, show examples, or tell you what you are missing.',
  ],
  tabs: [
    {
      id: 'planning',
      label: 'Planning',
      crumbs: ['RBT-200 Pallet robot', 'DRV-800 Drive module'],
      title: 'Pallet robot drive',
      brief: [
        ['Purpose', 'A drive module for a 1,000 kg pallet robot, 1.2 m/s, two shifts a day.'],
        ['Where it stands', 'MIL has proposed a plan that starts from the warehouse drive’s record.'],
        ['Your next step', 'Review the plan and accept it.'],
      ],
      traceLabel: 'Carried in, each with its trace',
      trace: [
        ['L-11', 'Compliant mount; gears sized on measured floor shock', 'TR-310 · TR-320'],
        ['L-04', 'Encoder on the wheel shaft', 'pallet mover, 2024'],
        ['ISS-12', 'Labyrinth and V-ring excluder', 'ISS-44'],
        ['GEAR-WEAR v1', '2 of the 3 correlations it needs to be trusted', 'TR-310 · TR-320'],
      ],
      planLabel: 'Proposed plan',
      plan: [
        ['Draft the requirements', 'MIL'],
        ['Approve the requirements', 'You'],
        ['Size the gears on the measured floor-shock spectrum', 'Engine'],
        ['Draft the drive with a compliant mount', 'MIL'],
        ['Build and measure on the wheel dyno', 'You'],
        ['Release Rev A', 'You · sign-in'],
      ],
      action: 'Accept plan',
    },
    {
      id: 'diagnosing',
      label: 'Diagnosing',
      crumbs: ['RBT-100 Warehouse robot', 'DRV-400 Drive module', 'WHL-430 Wheel and tread'],
      title: 'Wheel slip on the dock ramp · Rev B',
      brief: [
        ['Purpose', 'Explain the slip alarms and decide what to check first.'],
        ['Where it stands', 'Field report, Day 140: one robot logs wheel-slip alarms on the loading-dock ramp, only after the nightly wash-down.'],
        ['Your next step', 'Check the tread and the ramp coating before touching the drive.'],
      ],
      proposalLabel: 'MIL · proposed, awaiting your review',
      proposal: 'Start with the tread, not the drive. On this unit, Rev B’s backlash (0.3°) and gear-torque logs are inside limits, and the slip only happens on the wet ramp. The likely cause is the polyurethane tread losing grip on wet epoxy. Check tread hardness and wear, and the ramp coating, before changing anything in the drive.',
      groups: [
        {
          id: 'history',
          label: 'History on this Thread',
          rows: [
            ['TR-320', 'Backlash 0.3° steady over 1,000 h'],
            ['REQ-ENV-004', 'Nightly floor-scrubber wash-down'],
            ['WHL-430', '90 Shore A polyurethane tread'],
            ['GEAR-WEAR v1', '2 of 3 correlations'],
          ],
        },
        {
          id: 'similar',
          label: 'Similar past failures',
          rows: [
            ['ISS-21', 'Pallet mover, 2024. Wet-floor slip on a painted ramp. Fixed with a softer tread compound (85 Shore A) and a grit strip.'],
          ],
        },
        {
          id: 'assumptions',
          label: 'Assumptions to check',
          rows: [
            ['Payload', 'At or under 300 kg'],
            ['Ramp grade', 'At or under 5%'],
            ['Floor coating', 'Unchanged since the site was commissioned'],
            ['Tread', 'Within its wear limit'],
            ['Casters', 'Load balanced'],
          ],
        },
      ],
    },
  ],
};

/* ── 4 · WHERE FACTORY OS FITS — categories only, never products ─────────
   Agents: future tense only. */
export const FITS = {
  eyebrow: 'where factory os fits',
  headline: 'Beside your tools, never instead of them.',
  rows: [
    ['Authoring', 'Your CAD, ECAD and code are where designs are authored. Factory OS can draft geometry itself, and it works with the CAD you already use.'],
    ['Prediction meets reality', 'Simulation, test and manufacturing are where predictions meet reality. Their results come back to the decisions they were meant to prove.'],
    ['Production records', 'PLM, ERP and QMS keep production records, orders and the formal quality record. Factory OS references them and never replaces them.'],
  ],
  ours: ['Factory OS', 'Your PLM stores what was released. Factory OS keeps why it was built that way: for your team today, and for the AI agents that will work alongside them.'],
};

/* Close — the category line and the primary CTA. */
export const CLOSE = {
  headline: 'The guided engineering loop for hardware.',
  body: 'Open a project, describe the product, and walk it to Rev A — with the why kept, so Rev B starts ahead.',
};
